import { useRef } from 'react'

import * as Sentry from '@sentry/react'
import { FI_HELP_URL } from 'config/env'

import shieldIcon from 'app/assets/icons/application-details/shield.svg'
import bankIcon from 'app/assets/icons/bank.svg'
import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { ContainerHeader } from 'app/components/display/text/standard_text/standard-headings/component'
import { FI_UPGRADE_CTA_TEXT } from 'app/match/apply/application_summary_before_submit/components/manager_income_financials_container/financial-insights.constants'
import { monthlyToWeekly } from 'app/utils/financial/conversions'
import {
  formatCurrency,
  formatNextPay,
  formatPercentage,
  getMissingEssentialsLabel,
} from 'app/utils/financial/formatters'
import 'app/match/apply/application_summary_before_submit/components/manager_income_financials_container/style.scss'

const INCOME_COMPARISON_THRESHOLD = 0.15

function formatVerifiedDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  return `${String(d).padStart(2, '0')} ${months[m - 1]} ${y}`
}

interface VerifiedIncome {
  isRedacted?: boolean
  hasFinancialInsights: boolean
  verifiedMonthlyIncome?: number
  /** Mirrors Go fi.VerifiedIncome.VerifiedWeeklyIncome (json: verifiedWeeklyIncome). */
  verifiedWeeklyIncome?: number | null
  /** Mirrors Go fi.VerifiedIncome.BankName (json: bankName). */
  bankName?: string | null
  /** Mirrors Go fi.VerifiedIncome.AccountName (json: accountName). */
  accountName?: string | null
  verifiedSavingsTotal?: number
  rentToIncomeRatio?: number
  financialInsightsDate?: string
  /** Mirrors Go fi.VerifiedIncome.ReportID (json: financialInsightsReportID). Used by Sentry breadcrumb. */
  financialInsightsReportID?: string | null
  weeklySurplusAfterRentAndFinances?: number | null
  incomeStabilityDuration?: string | null
  incomeConfidence?: string | null
  nextPayDate?: string | null
  nextPayAmount?: number | null
  missingEssentialsGroceries?: boolean | null
  missingEssentialsTransport?: boolean | null
  missingEssentialsAccommodation?: boolean | null
}

interface Props {
  verifiedIncome?: VerifiedIncome | null
  selfReportedWeeklyIncome?: number | null
  /** Weekly listing rent in dollars; wired by F4. Undefined until then — ratio row hidden when null/undefined. */
  weeklyRent?: number | null
  displayedApplicant?: Record<string, unknown>
  onNewNoteUpdated?: () => void
  newNotesContent?: unknown
  onNoteIconClicked?: () => void
  onNewNoteAdded?: () => void
  onNoteCrossClicked?: () => void
  interpreteNoteResult?: Record<string, unknown>
  enableNotesButton?: boolean
  section?: string
  id?: string
  toggleRequestInfoModal?: () => void
  isManagerAdded?: boolean
}

function resolveWeekly(vi: VerifiedIncome): number | null {
  if (vi.verifiedWeeklyIncome != null) return vi.verifiedWeeklyIncome
  if (vi.verifiedMonthlyIncome != null) {
    return monthlyToWeekly(vi.verifiedMonthlyIncome)
  }
  return null
}

function getComparisonStatus(
  selfReportedWeekly?: number | null,
  verifiedWeekly?: number | null,
): 'differ' | 'match' | null {
  if (selfReportedWeekly == null || !verifiedWeekly) return null
  const diff = Math.abs(selfReportedWeekly - verifiedWeekly) / verifiedWeekly
  return diff > INCOME_COMPARISON_THRESHOLD ? 'differ' : 'match'
}

interface VerifiedMetricsProps {
  verifiedIncome: VerifiedIncome
  verifiedWeekly: number | null
  isWeeklyApprox: boolean
  selfReportedWeeklyIncome?: number | null
  rentToIncomeRatio: number | null
  comparison: 'differ' | 'match' | null
}

function VerifiedMetrics({
  verifiedIncome,
  verifiedWeekly,
  isWeeklyApprox,
  selfReportedWeeklyIncome,
  rentToIncomeRatio,
  comparison,
}: VerifiedMetricsProps) {
  const nextPay = formatNextPay(
    verifiedIncome.nextPayDate,
    verifiedIncome.nextPayAmount,
  )
  const missingEssentials = getMissingEssentialsLabel(
    verifiedIncome.missingEssentialsGroceries,
    verifiedIncome.missingEssentialsTransport,
    verifiedIncome.missingEssentialsAccommodation,
  )

  return (
    <div className="application-container-padding">
      <div className="fi-pm-trust-mark" data-testid="fi-pm-trust-mark">
        <img src={shieldIcon} alt="" aria-hidden="true" />
        <span>Bank Verified Income</span>
        {verifiedIncome.financialInsightsDate && (
          <span className="fi-pm-verified-date">
            {` · ${formatVerifiedDate(verifiedIncome.financialInsightsDate)}`}
          </span>
        )}
        {FI_HELP_URL && (
          <a
            href={FI_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fi-pm-learn-more"
          >
            Learn more
          </a>
        )}
      </div>
      {(verifiedIncome.bankName || verifiedIncome.accountName) && (
        <div className="fi-pm-bank-account" data-testid="fi-pm-bank-account">
          <img
            src={bankIcon}
            alt=""
            aria-hidden="true"
            className="fi-pm-bank-icon"
          />
          <div className="fi-pm-bank-text">
            {verifiedIncome.bankName && (
              <div className="fi-pm-bank-name" data-testid="fi-pm-bank-name">
                {verifiedIncome.bankName}
              </div>
            )}
            {verifiedIncome.accountName && (
              <div
                className="fi-pm-account-name"
                data-testid="fi-pm-account-name"
              >
                {verifiedIncome.accountName}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="verified-metrics">
        {verifiedWeekly != null && (
          <div
            className="metric-row"
            data-testid={isWeeklyApprox ? 'fi-weekly-approximate' : undefined}
          >
            <span className="income-category income-label">
              Weekly income (after tax)
            </span>
            <span
              className="metric-value fi-metric-value-primary"
              data-testid="fi-pm-weekly-income"
              {...(isWeeklyApprox
                ? {
                    title:
                      'Approximate — converted from monthly value (CreditSense report predates weekly capture).',
                  }
                : {})}
            >
              {formatCurrency(verifiedWeekly)}
            </span>
          </div>
        )}
        {verifiedIncome.verifiedMonthlyIncome != null && (
          <div className="metric-row metric-row-secondary">
            <span className="income-category income-label income-label-secondary">
              Monthly equivalent
            </span>
            <span
              className="metric-value"
              data-testid="fi-pm-monthly-equivalent"
            >
              {formatCurrency(verifiedIncome.verifiedMonthlyIncome)}
            </span>
          </div>
        )}
        {verifiedIncome.verifiedSavingsTotal != null && (
          <div className="metric-row">
            <span className="income-category income-label">Savings total</span>
            <span className="metric-value" data-testid="fi-pm-savings-total">
              {formatCurrency(verifiedIncome.verifiedSavingsTotal)}
            </span>
          </div>
        )}
        {rentToIncomeRatio != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Rent-to-income ratio (weekly)
            </span>
            <span
              className="metric-value"
              data-testid="fi-pm-rent-to-income-weekly"
            >
              {formatPercentage(rentToIncomeRatio)}
            </span>
          </div>
        )}
        {verifiedIncome.weeklySurplusAfterRentAndFinances != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Weekly surplus after rent &amp; commitments
            </span>
            <span className="metric-value" data-testid="fi-pm-weekly-surplus">
              {formatCurrency(verifiedIncome.weeklySurplusAfterRentAndFinances)}
              /wk
            </span>
          </div>
        )}
        {verifiedIncome.incomeStabilityDuration != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Income stability
            </span>
            <span className="metric-value" data-testid="fi-pm-income-stability">
              {verifiedIncome.incomeStabilityDuration}
            </span>
          </div>
        )}
        {verifiedIncome.incomeConfidence != null && (
          <div className="metric-row">
            <span className="income-category income-label">
              Income confidence
            </span>
            <span
              className="metric-value"
              data-testid="fi-pm-income-confidence"
            >
              {verifiedIncome.incomeConfidence}
            </span>
          </div>
        )}
        {nextPay != null && (
          <div className="metric-row">
            <span className="income-category income-label">Next pay</span>
            <span className="metric-value">{nextPay}</span>
          </div>
        )}
        <div className="metric-row">
          <span className="income-category income-label">
            Missing essentials
          </span>
          <span className="metric-value" data-testid="fi-pm-missing-essentials">
            {missingEssentials}
          </span>
        </div>
      </div>

      {comparison && (
        <div className="income-comparison">
          <span className="income-category income-label">
            Income comparison
          </span>
          <div className="comparison-rows">
            <div className="comparison-row">
              <span className="comparison-label">Self-reported:</span>
              <span className="comparison-value">
                {formatCurrency(selfReportedWeeklyIncome ?? null)}/wk
              </span>
            </div>
            <div className="comparison-row">
              <span className="comparison-label">Verified:</span>
              <span className="comparison-value">
                {formatCurrency(verifiedWeekly)}/wk
              </span>
            </div>
            <div className={`comparison-indicator ${comparison}`}>
              {comparison === 'match'
                ? 'Income figures are consistent'
                : 'Income figures differ significantly'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ManagerIncomeFinancialsContainer = ({
  verifiedIncome,
  selfReportedWeeklyIncome,
  weeklyRent,
  displayedApplicant,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  section = 'verifiedFinancials',
  id = '',
  toggleRequestInfoModal,
  isManagerAdded,
}: Props) => {
  // Hoist verifiedWeekly + isWeeklyApprox once so headline, comparison block, and
  // rent-to-income ratio all read the same resolved value (AC-P3, AC-P5, AC-P7).
  const verifiedWeekly = verifiedIncome ? resolveWeekly(verifiedIncome) : null
  const isWeeklyApprox =
    verifiedIncome != null &&
    verifiedIncome.verifiedWeeklyIncome == null &&
    verifiedWeekly != null

  // Sentry breadcrumb: fires once on first render when FI data exists but date is missing.
  // Uses useRef guard so it is idempotent across re-renders and observable under enzyme shallow
  // (which does NOT fire useEffect — see design.md "Telemetry (AC-P4)").
  const sentryFiredRef = useRef(false)
  if (
    !sentryFiredRef.current &&
    verifiedIncome?.hasFinancialInsights &&
    !verifiedIncome.financialInsightsDate
  ) {
    sentryFiredRef.current = true
    Sentry.addBreadcrumb({
      category: 'fi',
      message: 'fi.pm_panel.empty_date',
      level: 'info',
      data: { reportId: verifiedIncome.financialInsightsReportID ?? null },
    })
  }

  if (!verifiedIncome) return null
  if (!verifiedIncome.isRedacted && !verifiedIncome.hasFinancialInsights)
    return null

  const comparison = verifiedIncome.isRedacted
    ? null
    : getComparisonStatus(selfReportedWeeklyIncome, verifiedWeekly)

  const rentToIncomeRatio =
    weeklyRent != null && verifiedWeekly != null && verifiedWeekly > 0
      ? weeklyRent / verifiedWeekly
      : null

  return (
    <div
      id={id}
      className="container-component manager-income-financials-container application-summary-card bbs"
    >
      <ContainerHeader
        icon={incomeIcon}
        text="Verified Financial Insights"
        isEditable={false}
        displayedApplicant={displayedApplicant}
        onNewNoteUpdated={onNewNoteUpdated}
        newNotesContent={newNotesContent}
        onNoteIconClicked={onNoteIconClicked}
        onNewNoteAdded={onNewNoteAdded}
        onNoteCrossClicked={onNoteCrossClicked}
        interpreteNoteResult={interpreteNoteResult}
        enableNotesButton={enableNotesButton}
        section={section}
        toggleRequestInfoModal={toggleRequestInfoModal}
        isManagerAdded={isManagerAdded}
        toggleEditRenterApplication={undefined}
        targetGUID={undefined}
        iconHeaderCmp={undefined}
      />
      {verifiedIncome.isRedacted ? (
        <div className="application-container-padding">
          <p className="fi-upgrade-cta">{FI_UPGRADE_CTA_TEXT}</p>
        </div>
      ) : (
        <VerifiedMetrics
          verifiedIncome={verifiedIncome}
          verifiedWeekly={verifiedWeekly}
          isWeeklyApprox={isWeeklyApprox}
          selfReportedWeeklyIncome={selfReportedWeeklyIncome ?? null}
          rentToIncomeRatio={rentToIncomeRatio}
          comparison={comparison}
        />
      )}
    </div>
  )
}

export default ManagerIncomeFinancialsContainer
